import React, { useState } from 'react';
import Datetime from 'react-datetime';
import { Global } from '@emotion/core';
import { css } from 'emotion';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
  Path,
} from 'react-hook-form';
import { Caret } from 'shared/Caret';
import { useT } from 'client/i18n/hooks/useT';
import { useLocale } from 'client/i18n/hooks/useLocale';
import { Modal } from '@spotify-internal/anchor-ds';
import { ButtonPrimary, ButtonSecondary } from '@spotify-internal/encore-web';
import {
  Container,
  Dropdown,
  DropdownWrapper,
  LocalTimezoneMessage,
  PseudoLabel,
  PublishDateDisplay,
  DatetimeGlobalStyles,
} from './styles';
import {
  getDisplayValue,
  roundToNextHour,
  getCurrentDateAtUpcomingHour,
} from './utils';

export const ControlledFieldDatePicker = <T extends FieldValues>({
  label,
  name = 'publishOn' as Path<T>,
  className,
  control,
}: {
  label: string;
  name: FieldPath<T>;
  className?: any;
  control?: Control<T>;
}) => {
  const [isShowingSchedulerModal, setIsShowingSchedulerModal] = useState(false);
  const {
    field: { value, onChange },
  } = useController<T, FieldPath<T>>({ control, name });
  const [valueOnOpen, setValueOnOpen] = useState(value);
  const t = useT();
  const { locale } = useLocale();

  const dateTimeValue =
    !value || value === '' ? getCurrentDateAtUpcomingHour() : value;

  return (
    <Container className={className}>
      <PseudoLabel aria-label={name}>{label}</PseudoLabel>
      <DropdownWrapper>
        <button
          onClick={() => {
            setIsShowingSchedulerModal(true);
            setValueOnOpen(value);
          }}
          type="button"
          aria-label={t(
            'FieldDatePicker.ButtonAriaLabel',
            'Open date selector',
            'Date picker button screenreader information'
          )}
        >
          <Dropdown>
            <PublishDateDisplay>
              {getDisplayValue(
                value,
                null,
                t(
                  'FieldDatePicker.NowLabel',
                  'Now',
                  'Episode publish now label'
                )
              )}
            </PublishDateDisplay>
            <Caret color="#5f6369" />
          </Dropdown>
        </button>
      </DropdownWrapper>
      <Modal
        size="small"
        aria-labelledby="date-picker-title"
        isShowing={isShowingSchedulerModal}
        onClose={() => {
          onChange(valueOnOpen);
          setIsShowingSchedulerModal(false);
        }}
        isShowingCloseButton
      >
        <Modal.Title id="date-picker-title">
          {getDisplayValue(
            value,
            t(
              'FieldDatePicker.ScheduleForLabel',
              'Schedule for',
              'Epsiode schedule for label'
            ),
            t(
              'FieldDatePicker.PublishNowLabel',
              'Publish now',
              'Episode publish now label'
            )
          )}
        </Modal.Title>
        <Modal.Body
          as="div"
          className={css`
            width: 100%;
            max-width: 300px;
          `}
        >
          <Global styles={DatetimeGlobalStyles} />
          <Datetime
            locale={locale}
            className="DatetimeContainer"
            input={false}
            timeConstraints={{
              minutes: { min: 0, max: 0, step: 60 },
              seconds: { min: 0, max: 0, step: 60 },
              milliseconds: { min: 0, max: 0, step: 1000 },
            }}
            onChange={date => {
              onChange(roundToNextHour(date));
            }}
            value={dateTimeValue}
            aria-labelledby="date-picker-title"
          />
          <LocalTimezoneMessage>
            {t(
              'FieldDatePicker.LocalTimeZonesMessage',
              'All times are local',
              'Date picker local time zones message'
            )}
          </LocalTimezoneMessage>
        </Modal.Body>
        <Modal.Footer>
          <ButtonSecondary
            onClick={() => {
              onChange(null);
              setIsShowingSchedulerModal(false);
            }}
          >
            {t(
              'FieldDatePicker.ResetToNowButton',
              'Reset to now',
              'Date picker reset to now button text'
            )}
          </ButtonSecondary>
          <ButtonPrimary onClick={() => setIsShowingSchedulerModal(false)}>
            {t('Button.Confirm', 'Confirm', 'Confirm button text')}
          </ButtonPrimary>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
